import React from 'react';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const fundingExperts = [
  {
    name: "Mark Price",
    title: "Founder & CEO",
    email: "mark@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/s5aoi64w_Mark%20Price.png",
    description: "With over 20 years in the waste management industry, Mark founded Waste Loan Bros to help contractors get the equipment financing they deserve."
  },
  {
    name: "Eric Price",
    title: "Co-Founder & COO",
    email: "eric@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/pcb4zogx_Eric%20Price.png",
    description: "Eric brings 15+ years of operations expertise, ensuring every loan process runs smoothly from application to funding."
  },
  {
    name: "George Burgess",
    title: "President",
    email: "george@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/l3y04mk0_George%20Burgess.png",
    description: "George specializes in large equipment financing and has helped fund over $50M in waste management equipment."
  }
];

const backbone = [
  {
    name: "Chrissy Gomes",
    title: "Client Relations Manager",
    email: "chrissy@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/05hbu79d_Chrissy%20Gomes.png",
    description: "Chrissy manages client relationships and ensures ongoing support throughout the entire financing journey."
  },
  {
    name: "Tina Hines",
    title: "Operations Coordinator",
    email: "tina@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/swes9lad_Tina%20Hines.png",
    description: "Tina keeps everything running smoothly behind the scenes, coordinating operations and ensuring seamless communication across all departments."
  },
  {
    name: "Amy Deason",
    title: "Loan Specialist",
    email: "amy@wasteloanbros.com",
    image: "https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/nyl0vdot_Amy%20Deason.png",
    description: "Amy's attention to detail and customer-first approach ensures every client gets personalized financing solutions."
  }
];

const TeamMemberCard = ({ member, index, isReversed }) => (
  <div 
    className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-16 items-center`}
  >
    {/* Image */}
    <div className="w-full lg:w-1/3 flex justify-center">
      <div className="relative group">
        <div className="w-72 h-72 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-xl bg-white border-4 border-[#00A86B]/20 group-hover:border-[#00A86B] transition-colors duration-300">
          <img 
            src={member.image} 
            alt={member.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="w-full lg:w-2/3">
      <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-[#00A86B]">
        <h2 className="text-3xl md:text-4xl font-black text-[#0A1628] mb-2">
          {member.name.toUpperCase()}
        </h2>
        <p className="text-[#00A86B] font-bold text-lg mb-4">{member.title}</p>
        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
          {member.description}
        </p>
        
        {/* Contact Info */}
        <div className="flex flex-wrap gap-4 mb-6">
          <a 
            href={`mailto:${member.email}`}
            className="flex items-center gap-2 text-[#0A1628] hover:text-[#00A86B] transition-colors"
          >
            <Mail className="w-5 h-5" />
            <span className="font-medium">{member.email}</span>
          </a>
        </div>

        {/* CTA Button */}
        <Button 
          asChild
          className="bg-[#00A86B] hover:bg-[#008f5b] text-white font-bold px-6 py-3 rounded-full"
        >
          <a href={`mailto:${member.email}`}>
            EMAIL {member.name.split(' ')[0].toUpperCase()} NOW
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
      </div>
    </div>
  </div>
);

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center mb-16">
    <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-2">
      {title}
    </h2>
    {subtitle && (
      <p className="text-gray-600 text-lg">{subtitle}</p>
    )}
    <div className="w-24 h-1 bg-[#00A86B] mx-auto mt-4"></div>
  </div>
);

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#0A1628] relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/xh1uc1mn_Hero%20image.jpg')`,
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#00A86B] font-bold text-sm tracking-wider">OUR TEAM</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mt-4 mb-4">
            MEET THE
          </h1>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#00A86B] mb-6">
            BROS
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto">
            Real people who understand the waste management industry. We're here to help you grow.
          </p>
        </div>
      </section>

      {/* Your Equipment Funding Experts Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading 
            title="YOUR EQUIPMENT FUNDING EXPERTS" 
          />
          <div className="space-y-16">
            {fundingExperts.map((member, index) => (
              <TeamMemberCard 
                key={index} 
                member={member} 
                index={index}
                isReversed={index % 2 !== 0}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Backbone of Our Operation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading 
            title="BACKBONE OF OUR OPERATION" 
          />
          <div className="space-y-16">
            {backbone.map((member, index) => (
              <TeamMemberCard 
                key={index} 
                member={member} 
                index={index}
                isReversed={index % 2 !== 0}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#00A86B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            READY TO GET STARTED?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Contact any of our team members today and let's get your equipment funded.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-[#00A86B] hover:bg-gray-100 font-bold px-8 py-4 text-lg rounded-full"
            >
              <Link to="/">
                BACK TO HOME
              </Link>
            </Button>
            <Button 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#00A86B] font-bold px-8 py-4 text-lg rounded-full bg-transparent"
            >
              <Phone className="w-5 h-5 mr-2" />
              1-800-WASTEBRO
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
